import React, { useState, useEffect, useRef } from "react";

interface ThinkingModalProps {
  isOpen: boolean;
  message?: string;
  getThinkingSteps: () => string[];
}

const ThinkingModal: React.FC<ThinkingModalProps> = ({
  isOpen,
  message = "Smart Advisor is thinking...",
  getThinkingSteps,
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [displayedLines, setDisplayedLines] = useState<string[]>([]);
  const [currentTypingLine, setCurrentTypingLine] = useState("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const thinkingContainerRef = useRef<HTMLDivElement>(null);

  // Timer effect
  useEffect(() => {
    if (isOpen) {
      setElapsedTime(0);
      intervalRef.current = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1);
      }, 1000);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isOpen]);

  // Typing animation effect with random delay
  useEffect(() => {
    if (isOpen) {
      setDisplayedLines([]);
      setCurrentTypingLine("");
      const lines = getThinkingSteps();
      
      let lineIndex = 0;
      let charIndex = 0;

      const typeNextCharacter = () => {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

        if (lineIndex < lines.length) {
          const currentLine = lines[lineIndex];
          if (charIndex < currentLine.length) {
            setCurrentTypingLine(currentLine.substring(0, charIndex + 1));
            charIndex++;
            typingTimeoutRef.current = setTimeout(typeNextCharacter, 25); // Typing speed
          } else {
            // Line finished, add to displayed lines and introduce random delay before next line
            setDisplayedLines((prev) => [...prev, currentLine]);
            setCurrentTypingLine("");
            lineIndex++;
            charIndex = 0;
            const randomDelay = Math.floor(Math.random() * 2000) + 1000; // 1 to 3 seconds
            if (lineIndex < lines.length) {
              typingTimeoutRef.current = setTimeout(typeNextCharacter, randomDelay);
            }
          }
        } else {
          // All lines displayed
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        }
      };

      typeNextCharacter(); // Start typing

      return () => {
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      };
    }
  }, [isOpen, getThinkingSteps]);

  // Auto-scroll effect
  useEffect(() => {
    if (thinkingContainerRef.current) {
      thinkingContainerRef.current.scrollTop = thinkingContainerRef.current.scrollHeight;
    }
  }, [displayedLines, currentTypingLine]);

  if (!isOpen) {
    return null;
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${minutes}:${secs}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-2xl p-6 w-full max-w-3xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-poppins font-bold text-xl text-warning">{message}</h3>
          <div className="font-mono font-semibold text-lg text-darkBlue bg-offWhite px-3 py-1 rounded-md">
            {formatTime(elapsedTime)}
          </div>
        </div>

        {/* Terminal Display - Green text uses inline style for visibility */}
        <div
          ref={thinkingContainerRef}
          className="p-4 bg-black rounded-lg h-96 overflow-y-auto font-mono text-sm whitespace-pre-wrap"
          style={{ color: '#4ade80' }}
        >
          {displayedLines.length === 0 && !currentTypingLine && (
            <p className="mb-1" style={{ color: '#4ade80' }}>Initializing...</p>
          )}
          {displayedLines.map((line, index) => (
            <p key={index} className="mb-1" style={{ color: '#4ade80' }}>
              {line}
            </p>
          ))}
          {currentTypingLine && (
            <p className="mb-1" style={{ color: '#4ade80' }}>
              {currentTypingLine}
              <span 
                className="inline-block w-2 h-4 ml-1 align-text-bottom animate-pulse"
                style={{ backgroundColor: '#4ade80' }}
              ></span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThinkingModal;
